import {
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useEffect, useState, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import {
  createNalcoRate,
  deleteNalcoRate,
  fetchNalcoRateList,
  updateNalco,
} from './../../slices/nalco-rate';
import { createNumberValidationRule } from '../../utils/CreateNumberValidationRule';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import eventBus from '../../utils/eventBus';
import logo from '../../Images/alux-logo.png';

function Nalco() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [search, setSearch] = useState(localStorage.getItem('nalcoList') || '');
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('nalcoList') || '',
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'date',
    'ignot_grade',
    'rate',
    'rate_per_mt',
    'difference',
    'action',
  ]); // Initial columns to display


  const { user: currentUser } = useSelector(state => state.auth);
  const { data = [], totalRecord, status} = useSelector(state => state.nalcoRate || {});
  const ipAddress = useSelector(state => state.ip.ipAddress);
  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const fetchNalcoList = useCallback(() => {
    dispatch(
      fetchNalcoRateList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchNalcoList();
  }, [fetchNalcoList]);

  useEffect(() => {
    const handleReload = () => {

      fetchNalcoList();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchNalcoList]);


  const onEdit = record => {
    setEditingRecord(record);
    form.setFieldsValue({
      date: dayjs(record.date),
      ignot_grade: record.ignot_grade,
      rate: record.rate,
      rate_per_mt: record.rate_per_mt,
    });
    setIsDrawerVisible(true);
  };
  const onFinish = async values => {
    try {
      if (editingRecord) {
        values.date = dayjs(values.date).format('YYYY-MM-DD');
        await dispatch(updateNalco({ id: editingRecord.id, values })).unwrap();
        message.success('Nalco Rate updated successfully!');
      } else {
        values.date = dayjs(values.date).format('YYYY-MM-DD');
        values.difference = (values.rate_per_mt / 1000).toFixed(2);
        values.deleted = 0;
        await dispatch(createNalcoRate(values)).unwrap();
        message.success('Nalco Rate added successfully!');
      }
      form.resetFields();
      setIsDrawerVisible(false);

      setEditingRecord(null);
      dispatch(
        fetchNalcoRateList({ page, pagesize, ...(search && { search: search }) }),
      );
    } catch (error) {
      if (typeof error === 'object' && error !== null) {
        const messages = [];
        for (const key in error) {
          if (Array.isArray(error[key])) {
            messages.push(`${key}: ${error[key].join(', ')}`);
          }
        }

        if (messages.length > 0) {
          message.error(messages.join('; '));
        } else {
          message.error('An unknown error occurred.');
        }
      } else if (typeof error === 'string') {
        message.error(error);
      } else if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('An unknown error occurred.');
      }
    }
  };
  const showDrawer = () => {
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('nalcoList', value); // save to localStorage
  };
  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable

    localStorage.removeItem('nalcoList');
    dispatch(fetchNalcoRateList({ page: 1, pagesize }));
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'date' && sortOrder === 'ascend') {
      sortingEnumKey = 'date';
    }
    if (sortField === 'date' && sortOrder === 'descend') {
      sortingEnumKey = '-date';
    }
    if (sortField === 'ignot_grade' && sortOrder === 'ascend') {
      sortingEnumKey = 'ignot_grade';
    }
    if (sortField === 'ignot_grade' && sortOrder === 'descend') {
      sortingEnumKey = '-ignot_grade';
    }
    if (sortField === 'rate' && sortOrder === 'ascend') {
      sortingEnumKey = 'rate';
    }
    if (sortField === 'rate' && sortOrder === 'descend') {
      sortingEnumKey = '-rate';
    }
    if (sortField === 'rate_per_mt' && sortOrder === 'ascend') {
      sortingEnumKey = 'rate_per_mt';
    }
    if (sortField === 'rate_per_mt' && sortOrder === 'descend') {
      sortingEnumKey = '-rate_per_mt';
    }
    if (sortField === 'difference' && sortOrder === 'ascend') {
      sortingEnumKey = 'difference';
    }
    if (sortField === 'difference' && sortOrder === 'descend') {
      sortingEnumKey = '-difference';
    }
    return sortingEnumKey;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteNalcoRate(id))
      .unwrap()
      .then(() => {
        message.success('Nalco Rate Archive Successfully!');
        dispatch(
          fetchNalcoRateList({
            page,
            pagesize,
            ...(search && { search: search }),
            ...(orderBy && { ordering: orderBy }),
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      render: record => {
        return (
          <span>{record ? dayjs(record).format('DD-MM-YYYY') : 'N/A'}</span>
        );
      },
    },
    {
      title: 'Ignot Grade',
      dataIndex: 'ignot_grade',
      key: 'ignot_grade',
      sorter: true,
    },

    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      sorter: true,
    },
    {
      title: 'Rate/ MT',
      dataIndex: 'rate_per_mt',
      key: 'rate_per_mt',
      sorter: true,
    },
    {
      title: 'Difference',
      dataIndex: 'difference',
      key: 'difference',
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          {currentUser.permissions.permissions.nalco.includes(
            'change_nalcomaster',
          ) && (
              <Link onClick={() => onEdit(record)}>
                <Tooltip title="Edit">
                  <EditIcon />
                </Tooltip>
              </Link>
            )}
          {currentUser.permissions.permissions.nalco.includes(
            'delete_nalcomaster',
          ) && (
              <Link>
                <Tooltip title="Archive">
                  <Popconfirm
                    title="Are you sure you want to archive this record?"
                    onConfirm={() => handleDelete(record.id)} // Trigger delete on confirmation
                    onCancel={() => message.info('Action cancelled')} // Optional cancel action
                    okText="Yes"
                    cancelText="No"
                  >
                    <Archive style={{ cursor: 'pointer' }} />
                  </Popconfirm>
                </Tooltip>
              </Link>
            )}
        </Space>
      ),
    },
  ];
  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };
  const dynamicColumns = columns.filter(column =>
    checkedList.includes(column.dataIndex || column.key),
  );
  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row xs={24} md={24}>
            <Col xs={12} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr No.
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="date">Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="ignot_grade">Ignot Grade</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="rate"> Rate</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="rate_per_mt">Rate/MT</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="difference">Difference</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const csvheaders = [
    { label: 'Sr. No', key: 'SrNo' },
    { label: 'Date', key: 'date' },
    { label: 'Ignot Grade', key: 'ignot_grade' },
    { label: 'Rate', key: 'rate' },
    { label: 'Rate/MT', key: 'rate_per_mt' },
    { label: 'difference', key: 'difference' },
    { label: 'created At', key: 'created_at' },
    { label: 'created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvData = data.map((item, index) => ({
    SrNo: index + 1,
    date: item.date,
    ignot_grade: item.ignot_grade,
    rate: item.rate,
    rate_per_mt: item.rate_per_mt,
    difference: item.difference,
    created_at: item.created_at || 'N/A',
    created_by: item.created_by
      ? `${item.created_by.first_name} ${item.created_by.last_name}`
      : 'N/A',
    updated_at: item.updated_at || 'N/A',
    updated_by: item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : 'N/A',
  }));

  const handlePdfDownload = async () => {
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Columns setup
    const pdfColumns = [
      { header: 'Sr No.', dataKey: 'SrNo' },
      { header: 'Date', dataKey: 'date' },
      { header: 'Ignot Grade', dataKey: 'ignot_grade' },
      { header: 'Difference', dataKey: 'difference' },
      { header: 'Rate', dataKey: 'rate' },
      { header: 'Rate/MT', dataKey: 'rate_per_mt' },
      { header: 'created At', dataKey: 'created_at' },
      { header: 'created By', dataKey: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by' },
    ];

    const pdffilteredColumns = pdfColumns.filter(column =>
      checkedList.includes(column.dataKey),
    );

    const rows = data.map((item, index) => ({
      SrNo: index + 1,
      date: item.date,
      ignot_grade: item.ignot_grade,
      rate: item.rate,
      rate_per_mt: item.rate_per_mt,
      difference: item.difference,
      created_at: item.created_at || 'N/A',
      created_by: item.created_by
        ? `${item.created_by.first_name} ${item.created_by.last_name}`
        : 'N/A',
      updated_at: item.updated_at || 'N/A',
      updated_by: item.updated_by
        ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
        : 'N/A',
    }));

    // Heading
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    const headingText = 'Nalco Rates';
    const headingX = pageWidth / 2 - pdf.getTextWidth(headingText) / 2;
    pdf.text(headingText, headingX, 40);

    // AutoTable
    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: 65,
      theme: 'grid',
      styles: {
        font: 'times',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',

      didDrawPage: data => {
        const pageNumber = pdf.internal.getNumberOfPages();
        const str = `Page ${pageNumber}`;
        const dateStr = new Date().toLocaleString();
        pdf.setFontSize(10);
        pdf.text(dateStr, 40, pageHeight - 20);
        pdf.text(str, pageWidth - 60, pageHeight - 20);
        if (pageNumber === 1) {
          pdf.addImage(logo, 'PNG', pageWidth - 100, 10, 90, 30);
        }
      },
    });

    pdf.save(`nalco${+new Date()}`);

    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Nalco Rate',
        ip_address: ipAddress,
        discription: 'Nalco rate pdf download successfully!',
      }),
    );
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Nalco Rate',
        ip_address: ipAddress,
        discription: 'Nalco rate excel download successfully!',
      }),
    );
  };
  return (
    <>
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>Nalco Rate</h2>
            <div className="bredcrumbwrp">
              <Link to="/quotation-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  //{ title: <Link to="/quotation-list">Quotations</Link> },
                   { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Nalco Rate' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={inputValue}
                style={{ width: 200 }}
                onChange={handleSearch}
              />
              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.nalco.includes(
                'download_nalco_rate_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'Nalco.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvData}
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.nalco.includes(
                'download_nalco_rate_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handlePdfDownload}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.nalco.includes(
                'add_nalcomaster',
              ) && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => showDrawer()}
                  >
                    <Tooltip title="Add Account Group">Add Nalco Rate</Tooltip>
                  </Button>
                )}
              {currentUser.permissions.permissions.nalco.includes(
                'view_nalcomaster',
              ) && (
                  <Link to="/archive-nalco" className="iconlink">
                    <Tooltip title="Archive">
                      <Archiveicon />
                    </Tooltip>
                  </Link>
                )}
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    visible={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}>
          <Table
            loading={status === 'loading'}
            columns={dynamicColumns}
            dataSource={dataSource}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: '600px' }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={['20', '50', '100']}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
      <Drawer
        title={editingRecord ? 'Edit Nalco Rate' : 'Add Nalco Rate'}
        width={400}
        onClose={closeDrawer}
        open={isDrawerVisible}
        styles={{
          body: { paddingBottom: 80 },
        }}
      >
        <Form
          layout="vertical"
          initialValues={{ date: dayjs() }}
          form={form}
          onFinish={onFinish}
        >
          <Col>
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: 'Please select nalco date' }]}
            >
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: '100%' }}
                getPopupContainer={trigger => trigger.parentElement}
              ></DatePicker>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="ignot_grade"
              label="Ignot Grade"
              rules={[
                { required: true, message: 'Please enter ignot grade' },
                {
                  pattern: /[a-zA-Z0-9/s]+$/,
                  message:
                    'Ignot Grade can only include letters, digits, and spaces!',
                },
              ]}
            >
              <Input
                placeholder="Please Enter Ignot Grade"
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="rate"
              label="Rate"
              rules={[
                { required: true, message: 'Please enter rate' },
                {
                  pattern: /^\d*\.?\d*$/,
                  message: 'Please enter only digits & decimal.',
                },
                createNumberValidationRule({
                  allowNegative: false,
                  maxBeforeDecimal: 10,
                  maxAfterDecimal: 3,
                  allowDecimal: true,
                }),
              ]}
            >
              <Input placeholder="Please Enter Rate" />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="rate_per_mt"
              label=" Rate / MT."
              rules={[
                { required: true, message: 'Please enter rate mt' },
                {
                  pattern: /^\d*\.?\d*$/,
                  message: 'Please enter only digits & decimal.',
                },
                createNumberValidationRule({
                  allowNegative: false,
                  maxBeforeDecimal: 7,
                  maxAfterDecimal: 2,
                  allowDecimal: true,
                }),
              ]}
            >
              <Input placeholder="Please Enter  Rate / MT" />
            </Form.Item>
          </Col>

          <div className="flex justify-end">
            <Form.Item>
              <Button type="primary" icon={<PlusOutlined />} htmlType="submit">
                {editingRecord ? 'Update Nalco Rate' : 'Create Nalco Rate'}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </>
  );
}

export default Nalco;
